import React from 'react';
import './icon.scss';
import { ReactSVG } from 'react-svg';
import { getPrefixedDataAttributes } from '@/components/utils/DomUtils';
import { resolveAssetUrl } from '@/utilities/CommonUtils';
import type { DataAttributes } from '@/components/utils/TypeUtils';

interface IconProps {
  /** SVG file to render. */
  source?: string;
  /** Size of the icon. */
  size: 'default' | 'small' | 'medium' | 'large';
  /** Specify an optional className to be added to your Icon. */
  className?: string;
  /** * Width of the icon. */
  width?: string;
  /** Height of the icon. */
  height?: string;
  /** Provide an optional function to be called when the Icon element is clicked. */
  onClick?: (e: React.MouseEvent<HTMLOrSVGElement>) => void;
  /** Provide an optional function to be called when the Icon element is hovered. */
  onMouseEnter?: (e: React.MouseEvent<HTMLOrSVGElement>) => void;
  /** Provide an optional function to be called when the Icon element is no longer hovered. */
  onMouseLeave?: (e: React.MouseEvent<HTMLOrSVGElement>) => void;
  /** The data-* attribute is used to store custom data private to the page or application. */
  dataAttributes?: DataAttributes;
}

/**
 * Icon is a visual symbol that indicates the purpose of an interface element. It’s used to represent ideas, content types, and actions.
 */
const Icon = ({
  source,
  size,
  className,
  width,
  height,
  onClick,
  onMouseEnter,
  onMouseLeave,
  dataAttributes,
  ...props
}: IconProps) => (
  <ReactSVG
    src={resolveAssetUrl(source)}
    httpRequestWithCredentials
    className={[`icon icon--${size}`, className].join(' ')}
    beforeInjection={(svg) => {
      svg.setAttribute('style', `width: ${width}; height: ${height}`);
    }}
    onClick={(e) => onClick && onClick(e)}
    onMouseEnter={(e) => onMouseEnter && onMouseEnter(e)}
    onMouseLeave={(e) => onMouseLeave && onMouseLeave(e)}
    {...getPrefixedDataAttributes(dataAttributes, 'Icon')}
    {...props}
  />
);

Icon.defaultProps = {
  source: null,
  size: 'default',
};

Icon.displayName = 'Icon';

export default React.memo(Icon);
